import { ANSWER_OPTIONS } from "@/assets/constants/constants";
import IconButton from "@/components/buttons/IconButton";
import { Icon } from "@/components/icons/Icon";
import InputSelect from "@/components/input-select/InputSelect";
import { ModalMobile } from "@/components/modal/mobile/ModalMobile";
import { Pill } from "@/components/pill/Pill";
import ProblemMessage from "@/components/problem-message/ProblemMessage";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import { createAnswer } from "@/lib/services/answers";
import { useDevice } from "@/store/useDevice";
import { usePhase } from "@/store/usePhase";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, type FC } from "react";
import { useParams } from "react-router-dom";

interface EmptyAnwerProps {
  questionId: string;
  initialStatus?: string;
}

const EmptyAnwer: FC<EmptyAnwerProps> = ({
  questionId,
  initialStatus = "",
}) => {
  const translate = useTranslate();
  const [viewAnswer, setViewAnswer] = useState(false);
  const [status] = useState(initialStatus);
  const [error, setError] = useState("");
  const { inspectionId } = useParams() as { inspectionId: string };
  const phase = usePhase((store) => store.currentPhase!);
  const queryClient = useQueryClient();
  const { isMobile } = useDevice();

  const queryKey = useMemo(() => `answers-${phase}`, []);

  const { mutateAsync: create } = useMutation({
    mutationFn: createAnswer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
  });

  const handleOnChange = async (newValue: string | null) => {
    try {
      create({
        body: {
          questionId,
          status: newValue as string,
          comment: "",
          images: [],
        },
        inspectionId: Number(inspectionId),
        phaseId: phase,
      });
    } catch (error) {
      const e = error as Error;
      setError(e.message);
    }
  };

  const handleOnCloseError = () => {
    setError("");
  };

  return (
    <>
      {error && (
        <ProblemMessage closeMessage={handleOnCloseError} error={error} />
      )}
      <div
        className={`flex flex-col gap-2 bg-white p-2 rounded-sm md:border md:border-ib-gray-lightest md:h-32`}
      >
        <div>
          <Pill
            icon="pending"
            label={translate("pending")}
            className="bg-ib-gray-borders"
          />
        </div>
        <div>
          {isMobile ? (
            <div>
              <button
                type="button"
                onClick={() => setViewAnswer(true)}
                className="w-full text-left flex justify-between items-center border border-ib-gray-borders px-3 py-2 rounded-sm text-ib-gray-disabled"
              >
                <p> {translate("finishTask")}</p>
                <Icon name="fillArrowDown" className="text-ib-red-main" />
              </button>
            </div>
          ) : (
            <InputSelect
              value={status}
              onChange={handleOnChange}
              placeholder={translate("finishTask")}
              options={ANSWER_OPTIONS}
              id={"status-empty"}
            />
          )}
        </div>
      </div>
      {isMobile && viewAnswer && (
        <ModalMobile>
          <ModalMobile.Header className="border-b ">
            <div className="w-full flex justify-between items-center">
              <div className=" flex-1 flex items-center space-x-4">
                <h3 className="text-lg">{`${translate("finishTask")}`}</h3>
              </div>
              <IconButton
                icon="close"
                onClick={() => setViewAnswer(false)}
                iconClassName={"size-6"}
                className={"bg-transparent text-ib-red-main"}
              />
            </div>
          </ModalMobile.Header>
          <ModalMobile.Content className="bg-ib-gray-lightest-2">
            <div className="grid grid-rows-1 gap-2">
              {ANSWER_OPTIONS.map((option) => {
                return (
                  <button
                    onClick={() => handleOnChange(option.value)}
                    className=" w-full text-left border border-ib-gray-borders px-3 py-2 rounded-sm text-ib-gray-darkest"
                    key={option.value}
                    type="button"
                  >
                    <p>{option.label}</p>
                  </button>
                );
              })}
            </div>
          </ModalMobile.Content>
        </ModalMobile>
      )}
    </>
  );
};

export default EmptyAnwer;
